import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { SplashProvider } from './context/SplashContext';
import { CelebrationProvider } from './components/Celebration';
import { Layout, navSections } from './components/Layout';
import BiborneHealthBanner from './components/BiborneHealthBanner';
import WhatsAppNotifications from './components/WhatsAppNotifications';
import BorneClient from './pages/BorneClient';
import CrustThemeWrapper from './components/CrustThemeWrapper';
import './App.css';
import './boutique-theme.css';

// Récupération auto si un chunk lazy a disparu après un déploiement (cache périmé) : 1 reload max
const lazyRetry = (importer) => React.lazy(() => importer().then((m) => {
  sessionStorage.removeItem('chunk_reload');
  return m;
}).catch((err) => {
  if (!sessionStorage.getItem('chunk_reload')) {
    sessionStorage.setItem('chunk_reload', '1');
    window.location.reload();
    return new Promise(() => {});
  }
  throw err;
}));

const Login = lazyRetry(() => import('./pages/Login').then((m) => ({ default: m.Login })));
const Dashboard = lazyRetry(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Carte = lazyRetry(() => import('./pages/Carte').then((m) => ({ default: m.Carte })));
const Boutiques = lazyRetry(() => import('./pages/Boutiques').then((m) => ({ default: m.Boutiques })));
const Inspections = lazyRetry(() => import('./pages/Inspections').then((m) => ({ default: m.Inspections })));
const AuditV2New = lazyRetry(() => import('./pages/audit/AuditV2New'));
const AuditDetailRouter = lazyRetry(() => import('./pages/audit/AuditDetailRouter'));
const GrilleAdmin = lazyRetry(() => import('./pages/audit/GrilleAdmin'));
const SuiviAudits = lazyRetry(() => import('./pages/audit/SuiviAudits'));
const PlanningVisites = lazyRetry(() => import('./pages/audit/PlanningVisites'));
const GuideAudit = lazyRetry(() => import('./pages/audit/GuideAudit'));
const Observations = lazyRetry(() => import('./pages/audit/Observations'));
const TestsProduits = lazyRetry(() => import('./pages/audit/TestsProduits'));
const DeploymentHub = lazyRetry(() => import('./pages/deployment/DeploymentHub'));
const DeployItemDetail = lazyRetry(() => import('./pages/deployment/ItemDetail'));
const DeployTemplateDetail = lazyRetry(() => import('./pages/deployment/TemplateDetail'));
const DeployProjectDetail = lazyRetry(() => import('./pages/deployment/ProjectDetail'));
const DeployImportDetail = lazyRetry(() => import('./pages/deployment/ImportDetail'));
const Innovations = lazyRetry(() => import('./pages/Innovations').then((m) => ({ default: m.Innovations })));
const Formations = lazyRetry(() => import('./pages/Formations').then((m) => ({ default: m.Formations })));
const Alertes = lazyRetry(() => import('./pages/Alertes').then((m) => ({ default: m.Alertes })));
const Rentabilite = lazyRetry(() => import('./pages/Rentabilite').then((m) => ({ default: m.Rentabilite })));
const VentesRuptures = lazyRetry(() => import('./pages/VentesRuptures'));
const RapportTva = lazyRetry(() => import('./pages/RapportTva'));
const Guide = lazyRetry(() => import('./pages/Guide').then((m) => ({ default: m.Guide })));
const AIIntelligence = lazyRetry(() => import('./pages/AIIntelligence').then((m) => ({ default: m.AIIntelligence })));
const Reputation = lazyRetry(() => import('./pages/Reputation').then((m) => ({ default: m.Reputation })));
const UsersManagement = lazyRetry(() => import('./pages/UsersManagement').then((m) => ({ default: m.UsersManagement })));
const BiborneIntegration = lazyRetry(() => import('./pages/BiborneIntegration'));
const BorneV2 = lazyRetry(() => import('./pages/BorneV2'));
const Intelligence360 = lazyRetry(() => import('./pages/Intelligence360'));
const BorneV2Studio = lazyRetry(() => import('./pages/BorneV2Studio'));
const Bornes = lazyRetry(() => import('./pages/Bornes'));
const ComparatifVentes = lazyRetry(() => import('./pages/ComparatifVentes'));
const SaisieVentes = lazyRetry(() => import('./pages/SaisieVentes'));
const HistoriqueVentes = lazyRetry(() => import('./pages/HistoriqueVentes'));
const Mercuriale = lazyRetry(() => import('./pages/Mercuriale'));
const RentabiliteDashboard = lazyRetry(() => import('./pages/RentabiliteDashboard'));
const DashboardGlobal = lazyRetry(() => import('./pages/DashboardGlobal'));
const GestionCouts = lazyRetry(() => import('./pages/GestionCouts'));
const MenuImport = lazyRetry(() => import('./pages/MenuImport'));
const IntelligenceBoufi = lazyRetry(() => import('./pages/IntelligenceBoufi'));
const CooBoufi = lazyRetry(() => import('./pages/CooBoufi'));
const PredictionsTracker = lazyRetry(() => import('./pages/PredictionsTracker'));
const MarketingEvents = lazyRetry(() => import('./pages/MarketingEvents'));
const BoufiBrain = lazyRetry(() => import('./pages/BoufiBrain'));
const OpeningHours = lazyRetry(() => import('./pages/OpeningHours'));
const DashboardBiborne = lazyRetry(() => import('./pages/DashboardBiborne'));
const ExecutiveCoach = lazyRetry(() => import('./pages/exec/ExecutiveCoach'));
const BiborneDatabase = lazyRetry(() => import('./pages/BiborneDatabase'));
const ReferentielProduits = lazyRetry(() => import('./pages/ReferentielProduits'));
const MenuTemplates = lazyRetry(() => import('./pages/MenuTemplates'));
const ReferentielClients = lazyRetry(() => import('./pages/ReferentielClients'));
const DevenirAmbassadeur = lazyRetry(() => import('./pages/DevenirAmbassadeur'));
const FoodCostDashboard = lazyRetry(() => import('./pages/FoodCostDashboard'));
const PriceSimulator = lazyRetry(() => import('./pages/PriceSimulator'));
const PromoCalculator = lazyRetry(() => import('./pages/PromoCalculator'));
const CampaignReports = lazyRetry(() => import('./pages/CampaignReports'));
const UberEatsDashboard = lazyRetry(() => import('./pages/UberEatsDashboard'));
const MultiCanalDashboard = lazyRetry(() => import('./pages/MultiCanalDashboard'));
const StockDashboard = lazyRetry(() => import('./pages/StockDashboard'));
const KnowledgeBase = lazyRetry(() => import('./pages/KnowledgeBase'));
const ProductionPlan = lazyRetry(() => import('./pages/ProductionPlan'));
const KitchenLive = lazyRetry(() => import('./pages/KitchenLive'));
const Planning = lazyRetry(() => import('./pages/Planning'));
const Pointeuse = lazyRetry(() => import('./pages/Pointeuse'));
const ProdScreen = lazyRetry(() => import('./pages/ProdScreen'));
const SuiviPointeuse = lazyRetry(() => import('./pages/SuiviPointeuse'));
const CookieCounter = lazyRetry(() => import('./pages/CookieCounter'));
const StockFrigo = lazyRetry(() => import('./pages/StockFrigo'));
const StockVitrine = lazyRetry(() => import('./pages/StockVitrine'));
const GalerieCameras = lazyRetry(() => import('./pages/GalerieCameras'));
const BoufiLeague = lazyRetry(() => import('./pages/BoufiLeague'));
const BoufiFidelite = lazyRetry(() => import('./pages/BoufiFidelite'));
const FideliteCheck = lazyRetry(() => import('./pages/FideliteCheck'));
const PortalClient = lazyRetry(() => import('./pages/PortalClient'));
const PartenaireMACreation = lazyRetry(() => import('./pages/PartenaireMACreation'));
const PartenaireMADashboard = lazyRetry(() => import('./pages/PartenaireMADashboard'));
const CrustPortailApercu = lazyRetry(() => import('./pages/CrustPortailApercu'));
const CrustRapportRetrocession = lazyRetry(() => import('./pages/CrustRapportRetrocession'));
const BoutiqueKiosk = lazyRetry(() => import('./pages/BoutiqueKiosk'));
const FideliteAdmin = lazyRetry(() => import('./pages/FideliteAdmin'));
const FideliteParrainage = lazyRetry(() => import('./pages/FideliteParrainage'));
const FormationFidelite = lazyRetry(() => import('./pages/FormationFidelite'));
const MessagerieInterne = lazyRetry(() => import('./pages/MessagerieInterne'));
const InfluenceMarketing = lazyRetry(() => import('./pages/InfluenceMarketing'));
const ReglagesModules = lazyRetry(() => import('./pages/ReglagesModules'));
const CACorrections = lazyRetry(() => import('./pages/CACorrections'));
const WhatsAppManager = lazyRetry(() => import('./pages/WhatsAppManager'));
const WhatsAppInbox = lazyRetry(() => import('./pages/WhatsAppInbox'));
const WhatsAppTemplates = lazyRetry(() => import('./pages/WhatsAppTemplates'));
const CollectionsAdmin = lazyRetry(() => import('./pages/CollectionsAdmin'));
const ContratLicence = lazyRetry(() => import('./pages/ContratLicence'));
const DropExperience = lazyRetry(() => import('./pages/DropExperience'));
const DropLanding = lazyRetry(() => import('./pages/DropLanding'));
const DropWall = lazyRetry(() => import('./pages/DropWall'));
const BorneCaisse = lazyRetry(() => import('./pages/BorneCaisse'));
const CaisseComptoir = lazyRetry(() => import('./pages/CaisseComptoir'));
const DirectionLive = lazyRetry(() => import('./pages/DirectionLive'));
const BorneCommercial = lazyRetry(() => import('./pages/BorneCommercial'));
const CommandCenter = lazyRetry(() => import('./pages/CommandCenter'));
const BoufiIntelligence = lazyRetry(() => import('./pages/BoufiIntelligence'));
const Analytics360 = lazyRetry(() => import('./pages/Analytics360'));
const Academie = lazyRetry(() => import('./pages/Academie'));
const ChamasTacos = lazyRetry(() => import('./pages/chamas/ChamasTacos'));
const ModeDirigeant = lazyRetry(() => import('./pages/ModeDirigeant'));
const QualiteDonnees = lazyRetry(() => import('./pages/QualiteDonnees'));
const ControleCaisse = lazyRetry(() => import('./pages/caisse/ControleCaisse'));
const ShadowMode = lazyRetry(() => import('./pages/ShadowMode'));
const MappingBiborne = lazyRetry(() => import('./pages/MappingBiborne'));
const DashboardCanaux = lazyRetry(() => import('./pages/DashboardCanaux'));
const BorneVitrine = lazyRetry(() => import('./pages/BorneVitrine'));
const BorneBackBar = lazyRetry(() => import('./pages/BorneBackBar'));
const BackBarV2 = lazyRetry(() => import('./pages/BackBarV2'));
const Confidentialite = lazyRetry(() => import('./pages/Confidentialite'));
const RGPDAdmin = lazyRetry(() => import('./pages/RGPDAdmin'));
const SecurityCenter = lazyRetry(() => import('./pages/SecurityCenter'));
const AISeller = lazyRetry(() => import('./pages/AISeller'));
const Aujourdhui = lazyRetry(() => import('./pages/corner/Aujourdhui'));
const CornerCopilot = lazyRetry(() => import('./pages/corner/CornerCopilot'));
const CornerDetail = lazyRetry(() => import('./pages/corner/CornerDetail'));
const CornerParams = lazyRetry(() => import('./pages/corner/CornerParams'));
const ContratStudio = lazyRetry(() => import('./pages/corner/ContratStudio'));
const DemoProduction = lazyRetry(() => import('./pages/DemoProduction'));
const DemoCaisseV4 = lazyRetry(() => import('./pages/DemoCaisseV4'));
const DemoOuverture = lazyRetry(() => import('./pages/DemoOuverture'));
const AgentsDirectory = lazyRetry(() => import('./pages/corner/AgentsDirectory'));
const ArchitectPage = lazyRetry(() => import('./pages/corner/ArchitectPage'));
const CornerTeam = lazyRetry(() => import('./pages/corner/CornerTeam'));
const GuideCorner = lazyRetry(() => import('./pages/corner/GuideCorner'));
const BoufiNetwork = lazyRetry(() => import('./pages/network/BoufiNetwork'));
const StoreDetail = lazyRetry(() => import('./pages/network/StoreDetail'));
const BoutiqueCopilot = lazyRetry(() => import('./pages/corner/BoutiqueCopilot'));
const AuditMobile = lazyRetry(() => import('./pages/corner/AuditMobile'));
const CrustB2B = lazyRetry(() => import('./pages/crust/CrustB2B'));
const BorneEcran = lazyRetry(() => import('./pages/BorneEcran'));
const TvPlayer = lazyRetry(() => import('./pages/TvPlayer'));
const TvPair = lazyRetry(() => import('./pages/TvPair'));
const BornePair = lazyRetry(() => import('./pages/BornePair'));
const ActiverAppareil = lazyRetry(() => import('./pages/ActiverAppareil'));
const Installateur = lazyRetry(() => import('./pages/Installateur'));
const MenuBoard = lazyRetry(() => import('./pages/MenuBoard'));
const BorneStudio = lazyRetry(() => import('./pages/BorneStudio'));
const BorneMedias = lazyRetry(() => import('./pages/BorneMedias'));
const BorneDashboard = lazyRetry(() => import('./pages/BorneDashboard'));
const CommanderCC = lazyRetry(() => import('./pages/CommanderCC'));
const CCSuivi = lazyRetry(() => import('./pages/CCSuivi'));
const CCMerci = lazyRetry(() => import('./pages/CCMerci'));
const CartePromo = lazyRetry(() => import('./pages/CartePromo'));
const MomentsWeb = lazyRetry(() => import('./pages/MomentsWeb'));
const DropPreorder = lazyRetry(() => import('./pages/DropPreorder'));
const DropAdmin = lazyRetry(() => import('./pages/DropAdmin'));
const DropCaisse = lazyRetry(() => import('./pages/DropCaisse'));
const TirageLive = lazyRetry(() => import('./pages/TirageLive'));
const WaShop = lazyRetry(() => import('./pages/WaShop'));
const WaShopPaid = lazyRetry(() => import('./pages/WaShopPaid'));
const WaShopCancelled = lazyRetry(() => import('./pages/WaShopCancelled'));
const Cockpit = lazyRetry(() => import('./pages/Cockpit'));
const BackupsPage = lazyRetry(() => import('./pages/BackupsPage'));
const Pilotage = lazyRetry(() => import('./pages/pilotage/Pilotage'));
const Cuisine = lazyRetry(() => import('./pages/Cuisine'));
const CrustDashboard = lazyRetry(() => import('./pages/CrustDashboard'));
const CrustBanque = lazyRetry(() => import('./pages/CrustBanque'));
const CrustFacturesRecues = lazyRetry(() => import('./pages/CrustFacturesRecues'));
const CrustClientsFinaux = lazyRetry(() => import('./pages/CrustClientsFinaux'));
const CreatorsLanding = lazyRetry(() => import('./creators/pages/Landing'));
const CreatorsConfirmation = lazyRetry(() => import('./creators/pages/Confirmation'));
const CreatorsAdmin = lazyRetry(() => import('./pages/CreatorsAdmin'));
const MomentsHome = lazyRetry(() => import('./pages/MomentsHome'));
const MomentDiscover = lazyRetry(() => import('./pages/MomentDiscover'));
const MomentPerso = lazyRetry(() => import('./pages/MomentPerso'));
const MomentsAdmin = lazyRetry(() => import('./pages/MomentsAdmin'));
const HubEquipe = lazyRetry(() => import('./pages/HubEquipe'));
const CalendrierMensuel = lazyRetry(() => import('./pages/CalendrierMensuel'));
const CrustBDCDatabase = lazyRetry(() => import('./pages/CrustBDCDatabase'));
const ProduitsDatabase = lazyRetry(() => import('./pages/ProduitsDatabase'));
const CrustWorkflows = lazyRetry(() => import('./pages/CrustWorkflows'));
const CrustCircuits = lazyRetry(() => import('./pages/CrustCircuits'));
const CrustValidations = lazyRetry(() => import('./pages/CrustValidations'));
const CrustBonsCommande = lazyRetry(() => import('./pages/CrustBonsCommande'));
const CrustRapports = lazyRetry(() => import('./pages/CrustRapports'));
const CrustRapportsStats = lazyRetry(() => import('./pages/CrustRapportsStats'));
const CrustFactureNew = lazyRetry(() => import('./pages/CrustFactureNew'));
const CrustFactureDetail = lazyRetry(() => import('./pages/CrustFactureDetail'));
const CrustQontoDownloads = lazyRetry(() => import('./pages/CrustQontoDownloads'));
const CrustCRM = lazyRetry(() => import('./pages/CrustCRM'));
const CrustDevisNew = lazyRetry(() => import('./pages/CrustDevisNew'));
const CrustDevisDetail = lazyRetry(() => import('./pages/CrustDevisDetail'));
const CrustBCNew = lazyRetry(() => import('./pages/CrustBCNew'));
const JournalDirection = lazyRetry(() => import('./pages/JournalDirection'));
const Launches = lazyRetry(() => import('./pages/Launches'));
const LaunchDetail = lazyRetry(() => import('./pages/LaunchDetail'));
const LaunchesPlanning = lazyRetry(() => import('./pages/LaunchesPlanning'));
const CrustClients = lazyRetry(() => import('./pages/CrustSettings').then((m) => ({ default: m.CrustClients })));
const CrustProduits = lazyRetry(() => import('./pages/CrustSettings').then((m) => ({ default: m.CrustProduits })));
const CrustTarifs = lazyRetry(() => import('./pages/CrustSettings').then((m) => ({ default: m.CrustTarifs })));
const CrustFactures = lazyRetry(() => import('./pages/CrustSettings').then((m) => ({ default: m.CrustFactures })));
const WaRedirect = lazyRetry(() => import('./pages/WaRedirect'));
const KioskFidelite = lazyRetry(() => import('./pages/KioskFidelite'));
const DailyPerformance = lazyRetry(() => import('./pages/DailyPerformance'));
const BatchPlanning = lazyRetry(() => import('./pages/BatchPlanning'));
const EditionsLimitees = lazyRetry(() => import('./pages/EditionsLimitees'));
const LTOStrategicReport = lazyRetry(() => import('./pages/LTOStrategicReport'));
const LTOPreorders = lazyRetry(() => import('./pages/LTOPreorders'));
const APIKeysSettings = lazyRetry(() => import('./pages/APIKeysSettings'));
const ConnexionsIA = lazyRetry(() => import('./pages/ConnexionsIA'));
const ActionsIA = lazyRetry(() => import('./pages/ActionsIA'));
const BoutiquesSettings = lazyRetry(() => import('./pages/BoutiquesSettings'));
const BoutiqueOS = lazyRetry(() => import('./pages/BoutiqueOS'));
const EcranBoutique = lazyRetry(() => import('./pages/EcranBoutique'));
const NewContactsStats = lazyRetry(() => import('./pages/NewContactsStats'));
const CrossAnalysis = lazyRetry(() => import('./pages/CrossAnalysis'));
const StaffingOptimizer = lazyRetry(() => import('./pages/StaffingOptimizer'));
const AlertCockpit = lazyRetry(() => import('./pages/AlertCockpit'));
const LiaConsole = lazyRetry(() => import('./pages/LiaConsole'));
const LiaChat = lazyRetry(() => import('./pages/LiaChat'));
const ResetPage = lazyRetry(() => import('./pages/ResetPage'));
const LiaBrain = lazyRetry(() => import('./pages/LiaBrain'));
const MonthlyStrategicReport = lazyRetry(() => import('./pages/MonthlyStrategicReport'));
const AnalyseRentabilite = lazyRetry(() => import('./pages/AnalyseRentabilite'));
const FichesDePaie = lazyRetry(() => import('./pages/FichesDePaie'));
const ContentStudio = lazyRetry(() => import('./pages/ContentStudio'));
const BouffiAssistant = lazyRetry(() => import('./pages/BouffiAssistant'));
const RapportsJournaliers = lazyRetry(() => import('./pages/RapportsJournaliers'));
const AuditsHub = lazyRetry(() => import('./pages/audit-core/AuditsHub'));
const AuditRun = lazyRetry(() => import('./pages/audit-core/AuditRun'));
const AuditView = lazyRetry(() => import('./pages/audit-core/AuditView'));
const AuditAdminList = lazyRetry(() => import('./pages/audit-core/AuditAdminList'));
const OperationsHome = lazyRetry(() => import('./pages/OperationsHome'));
const ReseauHome = lazyRetry(() => import('./pages/reseau/ReseauHome'));
const Store360 = lazyRetry(() => import('./pages/reseau/Store360'));
const DoctrineTerrain = lazyRetry(() => import('./pages/reseau/DoctrineTerrain'));
const CoachBoard = lazyRetry(() => import('./pages/coach/CoachBoard'));
const CoachLauncher = lazyRetry(() => import('./pages/coach/CoachLauncher'));
const AlizeAujourdhui = lazyRetry(() => import('./pages/AlizeAujourdhui'));
const MissionsBoard = lazyRetry(() => import('./pages/MissionsBoard'));
const AgendaAlize = lazyRetry(() => import('./pages/AgendaAlize'));
const PilotageAlize = lazyRetry(() => import('./pages/PilotageAlize'));
const PiloteBoutique = lazyRetry(() => import('./pages/PiloteBoutique'));
const AuditTemplateEditor = lazyRetry(() => import('./pages/audit-core/AuditTemplateEditor'));
const Utilisateurs = lazyRetry(() => import('./pages/Utilisateurs'));
const KPIsPilotage = lazyRetry(() => import('./pages/KPIsPilotage'));
const RapportFranchiseur = lazyRetry(() => import('./pages/RapportFranchiseur'));
const WhatsAppBrief = lazyRetry(() => import('./pages/WhatsAppBrief'));
const SchedulersDashboard = lazyRetry(() => import('./pages/SchedulersDashboard'));
const CoutsIA = lazyRetry(() => import('./pages/CoutsIA'));
const RolesSettings = lazyRetry(() => import('./pages/RolesSettings'));
const IntelligentHome = lazyRetry(() => import('./pages/IntelligentHome'));
const AgentControlCenter = lazyRetry(() => import('./pages/AgentControlCenter'));
const PromoCodesAnalytics = lazyRetry(() => import('./pages/PromoCodesAnalytics'));
const WeeklyReports = lazyRetry(() => import('./pages/WeeklyReports'));
const TrackingLabels = lazyRetry(() => import('./pages/TrackingLabels'));
const AuditMarges = lazyRetry(() => import('./pages/AuditMarges'));
const UberEatsImport = lazyRetry(() => import('./pages/UberEatsImport'));

// Protected Route wrapper with permission check
// Trouve la premiere route accessible d'apres les permissions de l'utilisateur
// Index path -> item de menu : permet d'aligner l'accès aux routes sur la visibilité du menu
// pour les comptes à permissions custom (environnements sur mesure, ex. Alizé).
const NAV_BY_PATH = {};
navSections.forEach((s) => (s.items || []).forEach((it) => { if (it.path) NAV_BY_PATH[it.path] = it; }));

function customModuleAllows(user, pathname) {
  const custom = Array.isArray(user?.custom_permissions) && user.custom_permissions.length > 0 ? user.custom_permissions : null;
  if (!custom) return false;
  const item = NAV_BY_PATH[pathname];
  if (!item) return false;
  return custom.includes(item.moduleId || item.permission);
}

const ROUTE_PERMISSION_ORDER = [
  { path: '/dashboard', perm: 'dashboard' },
  { path: '/performance', perm: 'performance' },
  { path: '/dashboard-global', perm: 'dashboard_global' },
  { path: '/analyse-rentabilite', perm: 'rentabilite' },
  { path: '/rapports', perm: 'rapports' },
  { path: '/stock-frigo', perm: 'stock' },
  { path: '/planning', perm: 'planning' },
  { path: '/audit-hygiene', perm: 'audit_hygiene' },
  { path: '/inspections', perm: 'inspections' },
  { path: '/carte', perm: 'carte' },
  { path: '/boutiques', perm: 'boutiques' },
  { path: '/guide', perm: 'guide' },
  { path: '/boufi-ai', perm: 'corner_view' },
  { path: '/formations', perm: 'formations' },
  { path: '/reputation', perm: 'reputation' },
  { path: '/reseau', perm: 'reseau' },
  { path: '/comptage', perm: 'comptage_cookies' },
];

function computeFallback(user, hasPermission) {
  if (!user) return '/login';
  const raw = Array.isArray(user.custom_permissions) ? user.custom_permissions : null;
  const custom = raw && raw.length > 0 ? raw : null; // liste vide = pas de permissions custom
  if (!user.email?.endsWith('@boufi.internal') && (!custom || custom.includes('attention'))) return '/attention';
  if (user.role && user.role.startsWith('corner_') && hasPermission('corner_view')) return '/boufi-ai';
  if (user.role === 'manager') return '/comptage';
  if (custom && custom.includes('operations')) return '/operations';
  // Pour associe (ou tout user avec permissions limitees) : trouver la 1re route accessible
  for (const r of ROUTE_PERMISSION_ORDER) {
    if (hasPermission(r.perm)) return r.path;
  }
  // Jamais de cul-de-sac : /attention est accessible à tout utilisateur connecté
  return '/attention';
}

const ProtectedRoute = ({ children, roles, permission, bare }) => {
  const { user, loading, hasPermission } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0712' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5C00]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const fallback = computeFallback(user, hasPermission);
  // Si le module exact de cette page est dans les permissions custom du compte,
  // l'accès est accordé (cohérence avec la visibilité du menu) — évite les
  // redirections silencieuses vers le fallback.
  const moduleOverride = customModuleAllows(user, location.pathname);

  // Check roles (legacy) or permission (new system)
  if (roles && !roles.includes(user.role) && !moduleOverride) {
    // Eviter boucle infinie
    if (window.location.pathname === fallback) {
      return <NoAccessScreen />;
    }
    return <Navigate to={fallback} replace />;
  }

  if (permission && !hasPermission(permission) && !moduleOverride) {
    if (window.location.pathname === fallback) {
      return <NoAccessScreen />;
    }
    return <Navigate to={fallback} replace />;
  }

  const isMgr = ['admin', 'manager', 'direction'].includes(user.role);
  if (bare) return children;
  return <Layout>{isMgr && <WhatsAppNotifications />}{children}</Layout>;
};

// Ecran affiche si un utilisateur est connecte mais n'a AUCUN module accessible
function NoAccessScreen() {
  const { user, logout } = useAuth();
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: '#FAFAF7', fontFamily: "'DM Sans', sans-serif" }}>
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#FF8C4022' }}>
          <span style={{ color: '#FF8C40', fontSize: 32 }}>!</span>
        </div>
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "'Georgia', serif", color: '#1A1A1A' }}>
          Aucun module accessible
        </h1>
        <p className="text-sm mb-4" style={{ color: '#7A7060' }}>
          Bonjour {user?.name}, votre compte n'a aucune permission active. Contactez la Direction pour configurer vos acces.
        </p>
        <button onClick={logout} className="px-5 py-2 rounded-full text-sm font-medium" style={{ background: '#C9A55A', color: '#fff' }}>
          Deconnexion
        </button>
      </div>
    </div>
  );
}

// Fullscreen route (no sidebar) for kitchen display
const FullscreenRoute = ({ children, permission, roles }) => {
  const { user, loading, hasPermission } = useAuth();
  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5C00]" /></div>;
  if (!user) return <Navigate to="/login" replace />;
  if (permission && !hasPermission(permission)) return <Navigate to="/dashboard" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
};

// Public Route wrapper (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading, hasPermission } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0A0712' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF5C00]"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to={computeFallback(user, hasPermission)} replace />;
  }

  return children;
};

function AppRoutes() {
  const { hasPermission } = useAuth();
  const RootRedirect = () => {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (!user.email?.endsWith('@boufi.internal')) return <Navigate to="/attention" replace />;
    return <Navigate to={computeFallback(user, hasPermission)} replace />;
  };

  return (
    <>
      <CrustThemeWrapper />
      <React.Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
      <Routes>
      {/* Public routes */}
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/reset" element={<ResetPage />} />
      <Route path="/wa-shop/paid" element={<WaShopPaid />} />
      <Route path="/wa-shop/cancelled" element={<WaShopCancelled />} />
      
      {/* Protected routes with RBAC */}
      <Route path="/dashboard" element={<ProtectedRoute permission="dashboard"><Dashboard /></ProtectedRoute>} />
      <Route path="/cockpit" element={<ProtectedRoute roles={["admin","manager"]}><Cockpit /></ProtectedRoute>} />
      <Route path="/backups" element={<ProtectedRoute roles={["admin"]}><BackupsPage /></ProtectedRoute>} />
      <Route path="/pilotage" element={<ProtectedRoute roles={["admin"]} bare><Pilotage /></ProtectedRoute>} />
      <Route path="/cuisine" element={<ProtectedRoute roles={["admin","manager","direction","employee"]}><Cuisine /></ProtectedRoute>} />
      <Route path="/crust" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustDashboard /></ProtectedRoute>} />
      <Route path="/crust/b2b" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustB2B /></ProtectedRoute>} />
      <Route path="/crust/banque" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustBanque /></ProtectedRoute>} />
      <Route path="/crust/factures-recues" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustFacturesRecues /></ProtectedRoute>} />
      <Route path="/crust/clients-finaux" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustClientsFinaux /></ProtectedRoute>} />
      <Route path="/crust/portail" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustPortailApercu /></ProtectedRoute>} />
      <Route path="/crust/workflows" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustWorkflows /></ProtectedRoute>} />
      <Route path="/crust/circuits" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustCircuits /></ProtectedRoute>} />
      <Route path="/crust/validations" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustValidations /></ProtectedRoute>} />
      <Route path="/crust/bons-commande" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustBonsCommande /></ProtectedRoute>} />
      <Route path="/crust/bdc-database" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustBDCDatabase /></ProtectedRoute>} />
      <Route path="/crust/rapports" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustRapports /></ProtectedRoute>} />
      <Route path="/crust/rapports/stats" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustRapportsStats /></ProtectedRoute>} />
      <Route path="/crust/rapports/retrocession/generate" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustRapportRetrocession /></ProtectedRoute>} />
      <Route path="/crust/factures" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustFactures /></ProtectedRoute>} />
      <Route path="/crust/factures/new" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustFactureNew /></ProtectedRoute>} />
      <Route path="/crust/factures/:id" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustFactureDetail /></ProtectedRoute>} />
      <Route path="/crust/qonto-downloads" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustQontoDownloads /></ProtectedRoute>} />
      <Route path="/crust/crm" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustCRM /></ProtectedRoute>} />
      <Route path="/crust/devis/new" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustDevisNew /></ProtectedRoute>} />
      <Route path="/crust/devis/:id" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustDevisDetail /></ProtectedRoute>} />
      <Route path="/crust/bons-commande/new" element={<ProtectedRoute roles={["admin","manager","direction"]}><CrustBCNew /></ProtectedRoute>} />
      <Route path="/journal-direction" element={<ProtectedRoute roles={["admin","direction"]}><JournalDirection /></ProtectedRoute>} />
      <Route path="/launches" element={<ProtectedRoute roles={["admin","manager","direction"]}><Launches /></ProtectedRoute>} />
      <Route path="/launches/planning" element={<ProtectedRoute roles={["admin","manager","direction"]}><LaunchesPlanning /></ProtectedRoute>} />
      <Route path="/launches/:id" element={<ProtectedRoute roles={["admin","manager","direction"]}><LaunchDetail /></ProtectedRoute>} />
      <Route path="/crust/clients" element={<ProtectedRoute roles={["admin","direction"]}><CrustClients /></ProtectedRoute>} />
      <Route path="/crust/produits" element={<ProtectedRoute roles={["admin","direction"]}><CrustProduits /></ProtectedRoute>} />
      <Route path="/crust/tarifs" element={<ProtectedRoute roles={["admin","direction"]}><CrustTarifs /></ProtectedRoute>} />
      <Route path="/ca-corrections" element={<ProtectedRoute roles={["admin","manager"]}><CACorrections /></ProtectedRoute>} />
      <Route path="/whatsapp-manager" element={<ProtectedRoute roles={["admin","manager"]}><WhatsAppManager /></ProtectedRoute>} />
      <Route path="/inbox" element={<ProtectedRoute roles={["admin","manager"]}><WhatsAppInbox /></ProtectedRoute>} />
      <Route path="/whatsapp-inbox" element={<ProtectedRoute roles={["admin","manager"]}><WhatsAppInbox /></ProtectedRoute>} />
      <Route path="/whatsapp-templates" element={<ProtectedRoute roles={["admin"]}><WhatsAppTemplates /></ProtectedRoute>} />
      <Route path="/collections-admin" element={<ProtectedRoute roles={["admin","manager"]}><CollectionsAdmin /></ProtectedRoute>} />
      <Route path="/contrat-licence" element={<ProtectedRoute roles={["admin"]}><ContratLicence /></ProtectedRoute>} />
      <Route path="/d/:slug/:code" element={<DropExperience />} />
      <Route path="/lp/:slug" element={<DropLanding />} />
      <Route path="/bac" element={<DropLanding />} />
      <Route path="/mur/:slug" element={<DropWall />} />
      <Route path="/borne" element={<BorneClient />} />
      <Route path="/borne-v2" element={<BorneV2 />} />
      <Route path="/borne-v2-studio" element={<ProtectedRoute><BorneV2Studio /></ProtectedRoute>} />
      <Route path="/intelligence360" element={<ProtectedRoute><Intelligence360 /></ProtectedRoute>} />
      <Route path="/bornes" element={<ProtectedRoute><Bornes /></ProtectedRoute>} />
      <Route path="/demo-production" element={<DemoProduction />} />
      <Route path="/demo-caisse-v4" element={<DemoCaisseV4 />} />
      <Route path="/demo-ouverture" element={<DemoOuverture />} />
      <Route path="/borne-connect" element={<BornePair />} />
      <Route path="/activer/:pid" element={<ActiverAppareil />} />
      <Route path="/installer/:token" element={<Installateur />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/rgpd" element={<ProtectedRoute permission="users_manage"><RGPDAdmin /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute permission="users_manage"><SecurityCenter /></ProtectedRoute>} />
      <Route path="/ai-seller" element={<ProtectedRoute permission="planning"><AISeller /></ProtectedRoute>} />
      <Route path="/boufi-ai" element={<ProtectedRoute permission="corner_view"><CornerCopilot /></ProtectedRoute>} />
      <Route path="/aujourdhui" element={<ProtectedRoute permission="corner_view"><Aujourdhui /></ProtectedRoute>} />
      <Route path="/boufi-ai/corner/:id" element={<ProtectedRoute permission="corner_view"><CornerDetail /></ProtectedRoute>} />
      <Route path="/boufi-ai/parametres" element={<ProtectedRoute permission="corner_staff"><CornerParams /></ProtectedRoute>} />
      <Route path="/boufi-ai/contrats" element={<ProtectedRoute permission="corner_staff"><ContratStudio /></ProtectedRoute>} />
      <Route path="/boufi-ai/agents" element={<ProtectedRoute permission="corner_view"><AgentsDirectory /></ProtectedRoute>} />
      <Route path="/boufi-ai/architect" element={<ProtectedRoute permission="corner_staff"><ArchitectPage /></ProtectedRoute>} />
      <Route path="/boufi-ai/equipe" element={<ProtectedRoute permission="corner_staff"><CornerTeam /></ProtectedRoute>} />
      <Route path="/boufi-ai/guide" element={<ProtectedRoute permission="corner_view"><GuideCorner /></ProtectedRoute>} />
      <Route path="/boufi-network" element={<ProtectedRoute permission="boufi_network"><BoufiNetwork /></ProtectedRoute>} />
      <Route path="/deploiement" element={<ProtectedRoute permission="boufi_network"><DeploymentHub /></ProtectedRoute>} />
      <Route path="/deploiement/item/:id" element={<ProtectedRoute permission="boufi_network"><DeployItemDetail /></ProtectedRoute>} />
      <Route path="/deploiement/template/:id" element={<ProtectedRoute permission="boufi_network"><DeployTemplateDetail /></ProtectedRoute>} />
      <Route path="/deploiement/projet/:id" element={<ProtectedRoute permission="boufi_network"><DeployProjectDetail /></ProtectedRoute>} />
      <Route path="/deploiement/import/:id" element={<ProtectedRoute permission="boufi_network"><DeployImportDetail /></ProtectedRoute>} />
      <Route path="/boufi-network/store/:id" element={<ProtectedRoute permission="boufi_network"><StoreDetail /></ProtectedRoute>} />
      <Route path="/boufi-ai/boutique" element={<ProtectedRoute permission="boutique_copilot"><BoutiqueCopilot /></ProtectedRoute>} />
      <Route path="/boufi-ai/boutique/audit-mobile" element={<ProtectedRoute permission="boutique_copilot"><AuditMobile /></ProtectedRoute>} />
      <Route path="/commander" element={<CommanderCC />} />
      <Route path="/commander/suivi/:id" element={<CCSuivi />} />
      <Route path="/commander/merci" element={<CCMerci />} />
      <Route path="/carte/:id" element={<CartePromo />} />
      <Route path="/moments" element={<MomentsWeb />} />
      <Route path="/borne-ecran" element={<BorneEcran />} />
      <Route path="/tv" element={<TvPair />} />
      <Route path="/tv/:storeId" element={<TvPlayer />} />
      <Route path="/menu-board" element={<ProtectedRoute permission="reseau"><MenuBoard /></ProtectedRoute>} />
      <Route path="/borne-caisse" element={<ProtectedRoute permission="planning"><BorneCaisse /></ProtectedRoute>} />
      <Route path="/caisse-comptoir" element={<ProtectedRoute permission="planning" bare><CaisseComptoir /></ProtectedRoute>} />
      <Route path="/direction-live" element={<ProtectedRoute bare><DirectionLive /></ProtectedRoute>} />
      <Route path="/borne-commercial" element={<ProtectedRoute bare><BorneCommercial /></ProtectedRoute>} />
      <Route path="/command-center" element={<ProtectedRoute bare><CommandCenter /></ProtectedRoute>} />
      <Route path="/boufi-intelligence" element={<ProtectedRoute permission="dashboard"><BoufiIntelligence /></ProtectedRoute>} />
      <Route path="/analytics-360" element={<ProtectedRoute permission="dashboard"><Analytics360 /></ProtectedRoute>} />
      <Route path="/academie" element={<ProtectedRoute><Academie /></ProtectedRoute>} />
      <Route path="/dirigeant" element={<ProtectedRoute bare><ModeDirigeant /></ProtectedRoute>} />
      <Route path="/qualite-donnees" element={<ProtectedRoute bare><QualiteDonnees /></ProtectedRoute>} />
      <Route path="/controle-caisse" element={<ProtectedRoute permission="planning" bare><ControleCaisse /></ProtectedRoute>} />
      <Route path="/shadow" element={<ProtectedRoute permission="planning"><ShadowMode /></ProtectedRoute>} />
      <Route path="/mapping-biborne" element={<ProtectedRoute permission="planning"><MappingBiborne /></ProtectedRoute>} />
      <Route path="/dashboard-canaux" element={<ProtectedRoute permission="planning"><DashboardCanaux /></ProtectedRoute>} />
      <Route path="/borne-vitrine" element={<ProtectedRoute permission="planning"><BorneVitrine /></ProtectedRoute>} />
      <Route path="/borne-backbar" element={<ProtectedRoute permission="planning"><BorneBackBar /></ProtectedRoute>} />
      <Route path="/backbar-v2" element={<ProtectedRoute permission="planning" bare><BackBarV2 /></ProtectedRoute>} />
      <Route path="/borne-studio" element={<ProtectedRoute permission="planning"><BorneStudio /></ProtectedRoute>} />
      <Route path="/borne-studio/medias" element={<ProtectedRoute permission="planning"><BorneMedias /></ProtectedRoute>} />
      <Route path="/borne-dashboard" element={<ProtectedRoute permission="planning"><BorneDashboard /></ProtectedRoute>} />
      <Route path="/precommande/:token" element={<DropPreorder />} />
      <Route path="/drop-admin" element={<ProtectedRoute roles={["admin","manager"]}><DropAdmin /></ProtectedRoute>} />
      <Route path="/drop-caisse" element={<ProtectedRoute permission="planning"><DropCaisse /></ProtectedRoute>} />
      <Route path="/tirage-live" element={<ProtectedRoute roles={["admin","manager"]}><TirageLive /></ProtectedRoute>} />
      <Route path="/wa-shop" element={<ProtectedRoute roles={["admin","manager"]}><WaShop /></ProtectedRoute>} />
      <Route path="/wa" element={<WaRedirect />} />
      <Route path="/whatsapp" element={<WaRedirect />} />
      <Route path="/carte" element={<ProtectedRoute permission="carte"><Carte /></ProtectedRoute>} />
      <Route path="/boutiques" element={<ProtectedRoute permission="boutiques"><Boutiques /></ProtectedRoute>} />
      <Route path="/boutique-os" element={<ProtectedRoute><BoutiqueOS /></ProtectedRoute>} />
      <Route path="/inspections" element={<ProtectedRoute permission="inspections"><Inspections /></ProtectedRoute>} />
      <Route path="/inspections/new" element={<ProtectedRoute permission="inspections_create"><AuditV2New /></ProtectedRoute>} />
      <Route path="/inspections/grille" element={<ProtectedRoute roles={["admin"]}><GrilleAdmin /></ProtectedRoute>} />
      <Route path="/inspections/suivi" element={<ProtectedRoute permission="inspections"><SuiviAudits /></ProtectedRoute>} />
      <Route path="/inspections/planning" element={<ProtectedRoute permission="inspections"><PlanningVisites /></ProtectedRoute>} />
      <Route path="/inspections/guide" element={<ProtectedRoute permission="inspections"><GuideAudit /></ProtectedRoute>} />
      <Route path="/observations" element={<ProtectedRoute permission="inspections"><Observations /></ProtectedRoute>} />
      <Route path="/tests-produits" element={<ProtectedRoute permission="inspections"><TestsProduits /></ProtectedRoute>} />
      <Route path="/inspections/:id" element={<ProtectedRoute permission="inspections"><AuditDetailRouter /></ProtectedRoute>} />
      <Route path="/rentabilite" element={<ProtectedRoute permission="rentabilite"><Rentabilite /></ProtectedRoute>} />
      <Route path="/chamas" element={<ProtectedRoute permission="rentabilite"><ChamasTacos /></ProtectedRoute>} />
      <Route path="/chamas/:tab" element={<ProtectedRoute permission="rentabilite"><ChamasTacos /></ProtectedRoute>} />
      <Route path="/innovations" element={<ProtectedRoute permission="innovations"><Innovations /></ProtectedRoute>} />
      <Route path="/formations" element={<ProtectedRoute permission="formations"><Formations /></ProtectedRoute>} />
      <Route path="/alertes" element={<ProtectedRoute permission="alertes"><Alertes /></ProtectedRoute>} />
      <Route path="/guide" element={<ProtectedRoute permission="guide"><Guide /></ProtectedRoute>} />
      <Route path="/ai-intelligence" element={<ProtectedRoute permission="ai_intelligence"><AIIntelligence /></ProtectedRoute>} />
      <Route path="/reputation" element={<ProtectedRoute permission="reputation"><Reputation /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute permission="users_manage"><UsersManagement /></ProtectedRoute>} />
      <Route path="/biborne" element={<ProtectedRoute permission="rentabilite"><BiborneIntegration /></ProtectedRoute>} />
      <Route path="/comparatif-ventes" element={<ProtectedRoute permission="ai_intelligence"><ComparatifVentes /></ProtectedRoute>} />
      <Route path="/saisie-ventes" element={<ProtectedRoute permission="rentabilite"><SaisieVentes /></ProtectedRoute>} />
      <Route path="/historique-ventes" element={<ProtectedRoute permission="rentabilite"><HistoriqueVentes /></ProtectedRoute>} />
      <Route path="/mercuriale" element={<ProtectedRoute permission="rentabilite"><Mercuriale /></ProtectedRoute>} />
      <Route path="/rentabilite-dashboard" element={<ProtectedRoute permission="rentabilite"><RentabiliteDashboard /></ProtectedRoute>} />
      <Route path="/dashboard-global" element={<ProtectedRoute permission="rentabilite"><DashboardGlobal /></ProtectedRoute>} />
      <Route path="/gestion-couts" element={<ProtectedRoute permission="rentabilite"><GestionCouts /></ProtectedRoute>} />
      <Route path="/menu-import" element={<ProtectedRoute permission="rentabilite"><MenuImport /></ProtectedRoute>} />
      <Route path="/intelligence" element={<ProtectedRoute permission="rentabilite"><IntelligenceBoufi /></ProtectedRoute>} />
      <Route path="/coo" element={<ProtectedRoute permission="rentabilite"><CooBoufi /></ProtectedRoute>} />
      <Route path="/predictions" element={<ProtectedRoute permission="predictions"><PredictionsTracker /></ProtectedRoute>} />
      <Route path="/campagnes" element={<ProtectedRoute permission="predictions"><MarketingEvents /></ProtectedRoute>} />
      <Route path="/cerveau" element={<ProtectedRoute permission="rentabilite"><BoufiBrain /></ProtectedRoute>} />
      <Route path="/horaires" element={<ProtectedRoute permission="rentabilite"><OpeningHours /></ProtectedRoute>} />
      <Route path="/dashboard-biborne" element={<ProtectedRoute permission="rentabilite"><DashboardBiborne /></ProtectedRoute>} />
      <Route path="/executive-coach" element={<ProtectedRoute permission="rentabilite" bare><ExecutiveCoach /></ProtectedRoute>} />
      <Route path="/biborne-database" element={<ProtectedRoute permission="rentabilite"><BiborneDatabase /></ProtectedRoute>} />
      <Route path="/referentiel-produits" element={<ProtectedRoute permission="rentabilite"><ReferentielProduits /></ProtectedRoute>} />
      <Route path="/cartes" element={<ProtectedRoute permission="rentabilite"><MenuTemplates /></ProtectedRoute>} />
      <Route path="/referentiel-clients" element={<ProtectedRoute permission="rentabilite"><ReferentielClients /></ProtectedRoute>} />
      <Route path="/food-cost" element={<ProtectedRoute permission="rentabilite"><FoodCostDashboard /></ProtectedRoute>} />
      <Route path="/simulateur" element={<ProtectedRoute permission="rentabilite"><PriceSimulator /></ProtectedRoute>} />
      <Route path="/calculateur-promo" element={<ProtectedRoute permission="rentabilite"><PromoCalculator /></ProtectedRoute>} />
      <Route path="/rapport-campagnes" element={<ProtectedRoute permission="rentabilite"><CampaignReports /></ProtectedRoute>} />
      <Route path="/ubereats" element={<ProtectedRoute permission="rentabilite"><UberEatsDashboard /></ProtectedRoute>} />
      <Route path="/multicanal" element={<ProtectedRoute permission="rentabilite"><MultiCanalDashboard /></ProtectedRoute>} />
      <Route path="/stock" element={<ProtectedRoute permission="rentabilite"><StockDashboard /></ProtectedRoute>} />
      <Route path="/knowledge" element={<ProtectedRoute permission="dashboard"><KnowledgeBase /></ProtectedRoute>} />
      <Route path="/production" element={<ProtectedRoute permission="production_plan"><ProductionPlan /></ProtectedRoute>} />
      <Route path="/cuisine-live" element={<ProtectedRoute permission="rentabilite"><KitchenLive /></ProtectedRoute>} />
      <Route path="/planning" element={<ProtectedRoute permission="planning"><Planning /></ProtectedRoute>} />
      <Route path="/pointeuse" element={<FullscreenRoute roles={["admin"]}><Pointeuse /></FullscreenRoute>} />
      <Route path="/prod" element={<FullscreenRoute permission="planning"><ProdScreen /></FullscreenRoute>} />
      <Route path="/prod-live" element={<FullscreenRoute permission="planning"><ProdScreen forceLive /></FullscreenRoute>} />
      <Route path="/suivi-pointeuse" element={<ProtectedRoute roles={["admin"]}><SuiviPointeuse /></ProtectedRoute>} />
      <Route path="/comptage-cookies" element={<ProtectedRoute permission="comptage_cookies"><CookieCounter /></ProtectedRoute>} />
      <Route path="/comptage" element={<ProtectedRoute permission="comptage_cookies"><CookieCounter /></ProtectedRoute>} />
      <Route path="/stock-frigo" element={<ProtectedRoute permission="stock_frigo"><StockFrigo /></ProtectedRoute>} />
      <Route path="/stock-vitrine" element={<ProtectedRoute permission="comptage_cookies"><StockVitrine /></ProtectedRoute>} />
      <Route path="/galerie-cameras" element={<ProtectedRoute permission="galerie_cameras"><GalerieCameras /></ProtectedRoute>} />
      <Route path="/boufi-league" element={<ProtectedRoute roles={["admin"]}><BoufiLeague /></ProtectedRoute>} />
      <Route path="/fidelite" element={<ProtectedRoute permission="planning"><BoufiFidelite /></ProtectedRoute>} />
      <Route path="/fidelite-admin" element={<ProtectedRoute permission="fidelite_admin"><FideliteAdmin /></ProtectedRoute>} />
      <Route path="/formation-fidelite" element={<ProtectedRoute permission="fidelite_admin"><FormationFidelite /></ProtectedRoute>} />
      <Route path="/messagerie-interne" element={<ProtectedRoute permission="planning"><MessagerieInterne /></ProtectedRoute>} />
      <Route path="/influence-marketing" element={<ProtectedRoute permission="influence_marketing"><InfluenceMarketing /></ProtectedRoute>} />
      <Route path="/reglages" element={<ProtectedRoute permission="users_manage"><ReglagesModules /></ProtectedRoute>} />
      <Route path="/editions-limitees" element={<ProtectedRoute permission="editions_limitees"><EditionsLimitees /></ProtectedRoute>} />
      <Route path="/editions-limitees/:edId/rapport" element={<ProtectedRoute roles={["admin","manager"]}><LTOStrategicReport /></ProtectedRoute>} />
      <Route path="/editions-limitees/:ltoId/preorders" element={<ProtectedRoute roles={["admin","manager"]}><LTOPreorders /></ProtectedRoute>} />
      <Route path="/settings/api-keys" element={<ProtectedRoute roles={["admin","manager"]}><APIKeysSettings /></ProtectedRoute>} />
      <Route path="/settings/connexions-ia" element={<ProtectedRoute roles={["admin","manager"]}><ConnexionsIA /></ProtectedRoute>} />
      <Route path="/settings/actions-ia" element={<ProtectedRoute roles={["admin"]}><ActionsIA /></ProtectedRoute>} />
      <Route path="/settings/couts-ia" element={<ProtectedRoute roles={["admin"]}><CoutsIA /></ProtectedRoute>} />
      <Route path="/settings/roles" element={<ProtectedRoute roles={["admin"]}><RolesSettings /></ProtectedRoute>} />
      <Route path="/attention" element={<ProtectedRoute><IntelligentHome /></ProtectedRoute>} />
      <Route path="/operations" element={<ProtectedRoute><OperationsHome /></ProtectedRoute>} />
      <Route path="/reseau" element={<ProtectedRoute><ReseauHome /></ProtectedRoute>} />
      <Route path="/ma-journee" element={<ProtectedRoute><AlizeAujourdhui /></ProtectedRoute>} />
      <Route path="/missions" element={<ProtectedRoute><MissionsBoard /></ProtectedRoute>} />
      <Route path="/agenda" element={<ProtectedRoute><AgendaAlize /></ProtectedRoute>} />
      <Route path="/pilotage-alize" element={<ProtectedRoute permission="pilotage_alize"><PilotageAlize /></ProtectedRoute>} />
      <Route path="/pilotage-boutique" element={<ProtectedRoute><PiloteBoutique /></ProtectedRoute>} />
      <Route path="/pilotage-boutique/:storeId" element={<ProtectedRoute><PiloteBoutique /></ProtectedRoute>} />
      <Route path="/reseau/site/:sid" element={<ProtectedRoute><Store360 /></ProtectedRoute>} />
      <Route path="/reseau/doctrine" element={<ProtectedRoute><DoctrineTerrain /></ProtectedRoute>} />
      <Route path="/coach" element={<ProtectedRoute><CoachLauncher /></ProtectedRoute>} />
      <Route path="/coach/:storeId" element={<CoachBoard />} />
      <Route path="/settings/agents-control" element={<ProtectedRoute roles={["admin"]}><AgentControlCenter /></ProtectedRoute>} />
      <Route path="/settings/boutiques" element={<ProtectedRoute roles={["admin","manager"]}><BoutiquesSettings /></ProtectedRoute>} />
      <Route path="/inbox/new-contacts-stats" element={<ProtectedRoute roles={["admin","manager"]}><NewContactsStats /></ProtectedRoute>} />
      <Route path="/cross-analysis" element={<ProtectedRoute roles={["admin","manager"]}><CrossAnalysis /></ProtectedRoute>} />
      <Route path="/staffing-optimizer" element={<ProtectedRoute roles={["admin","manager"]}><StaffingOptimizer /></ProtectedRoute>} />
      <Route path="/alert-cockpit" element={<ProtectedRoute roles={["admin","manager"]}><AlertCockpit /></ProtectedRoute>} />
      <Route path="/lia-console" element={<ProtectedRoute roles={["admin","manager"]}><LiaConsole /></ProtectedRoute>} />
      <Route path="/lia-chat" element={<ProtectedRoute roles={["admin","manager"]}><LiaChat /></ProtectedRoute>} />
      <Route path="/lia-brain" element={<ProtectedRoute roles={["admin","manager"]}><LiaBrain /></ProtectedRoute>} />
      <Route path="/rapport-mensuel" element={<ProtectedRoute roles={["admin","manager"]}><MonthlyStrategicReport /></ProtectedRoute>} />
      <Route path="/rapport-mensuel/:period" element={<ProtectedRoute roles={["admin","manager"]}><MonthlyStrategicReport /></ProtectedRoute>} />
      <Route path="/analyse-rentabilite" element={<ProtectedRoute permission="rentabilite"><AnalyseRentabilite /></ProtectedRoute>} />
      <Route path="/ventes-ruptures" element={<ProtectedRoute permission="rentabilite"><VentesRuptures /></ProtectedRoute>} />
      <Route path="/rapport-tva" element={<ProtectedRoute permission="rentabilite"><RapportTva /></ProtectedRoute>} />
      <Route path="/fiches-de-paie" element={<ProtectedRoute permission="rentabilite"><FichesDePaie /></ProtectedRoute>} />
      <Route path="/content-studio" element={<ProtectedRoute permission="rentabilite"><ContentStudio /></ProtectedRoute>} />
      <Route path="/bouffi-assistant" element={<ProtectedRoute permission="formations"><BouffiAssistant /></ProtectedRoute>} />
      <Route path="/rapports" element={<ProtectedRoute permission="rapports"><RapportsJournaliers /></ProtectedRoute>} />
      <Route path="/audit-hygiene" element={<ProtectedRoute permission="audit_hygiene"><AuditsHub /></ProtectedRoute>} />
      <Route path="/audit-core/run/:id" element={<ProtectedRoute permission="audit_hygiene"><AuditRun /></ProtectedRoute>} />
      <Route path="/audit-core/view/:id" element={<ProtectedRoute permission="audit_hygiene"><AuditView /></ProtectedRoute>} />
      <Route path="/audit-core/admin" element={<ProtectedRoute permission="audit_hygiene"><AuditAdminList /></ProtectedRoute>} />
      <Route path="/audit-core/admin/:tid/:version" element={<ProtectedRoute permission="audit_hygiene"><AuditTemplateEditor /></ProtectedRoute>} />
      <Route path="/utilisateurs" element={<ProtectedRoute permission="users_manage"><Utilisateurs /></ProtectedRoute>} />
      <Route path="/kpis-pilotage" element={<ProtectedRoute permission="dashboard"><KPIsPilotage /></ProtectedRoute>} />
      <Route path="/rapport-franchiseur" element={<ProtectedRoute permission="scalabilite"><RapportFranchiseur /></ProtectedRoute>} />
      <Route path="/whatsapp-brief" element={<ProtectedRoute permission="users_manage"><WhatsAppBrief /></ProtectedRoute>} />
      <Route path="/schedulers" element={<ProtectedRoute permission="users_manage"><SchedulersDashboard /></ProtectedRoute>} />
      <Route path="/codes-promo" element={<ProtectedRoute permission="rentabilite"><PromoCodesAnalytics /></ProtectedRoute>} />
      <Route path="/rapport-hebdo" element={<ProtectedRoute permission="rentabilite"><WeeklyReports /></ProtectedRoute>} />
      <Route path="/tracabilite-etiquettes" element={<ProtectedRoute permission="audit_hygiene"><TrackingLabels /></ProtectedRoute>} />
      <Route path="/audit-marges" element={<ProtectedRoute permission="rentabilite"><AuditMarges /></ProtectedRoute>} />
      <Route path="/uber-eats-import" element={<ProtectedRoute permission="rentabilite"><UberEatsImport /></ProtectedRoute>} />
      <Route path="/performance" element={<ProtectedRoute permission="dashboard"><DailyPerformance /></ProtectedRoute>} />
      <Route path="/fournees" element={<ProtectedRoute permission="rentabilite"><BatchPlanning /></ProtectedRoute>} />
      <Route path="/fidelite/check" element={<FideliteCheck />} />
      <Route path="/fidelite/parrainage" element={<FideliteParrainage />} />
      <Route path="/portal" element={<PortalClient />} />
      <Route path="/partenaire/ma-creation" element={<PartenaireMACreation />} />
      <Route path="/partenaire/ma-creation/dashboard" element={<PartenaireMADashboard />} />
      <Route path="/devenir-ambassadeur" element={<DevenirAmbassadeur />} />
      <Route path="/kiosk" element={<KioskFidelite />} />
      <Route path="/boutique/:slug" element={<BoutiqueKiosk />} />
      <Route path="/boutique-ecran/:slug" element={<EcranBoutique />} />
      <Route path="/creators-admin" element={<ProtectedRoute roles={["admin","manager"]}><CreatorsAdmin /></ProtectedRoute>} />
      <Route path="/produits-db" element={<ProtectedRoute roles={["admin","manager"]}><ProduitsDatabase /></ProtectedRoute>} />
      <Route path="/creators" element={<CreatorsLanding />} />
      <Route path="/creators/welcome" element={<CreatorsConfirmation />} />
      <Route path="/moments/creer" element={<MomentsHome />} />
      <Route path="/moments" element={<MomentsHome />} />
      <Route path="/moments/perso/:code" element={<MomentPerso />} />
      <Route path="/moments/:code" element={<MomentDiscover />} />
      <Route path="/moments-admin" element={<ProtectedRoute roles={["admin"]}><MomentsAdmin /></ProtectedRoute>} />
      <Route path="/hub-equipe" element={<ProtectedRoute roles={["admin","manager"]}><HubEquipe /></ProtectedRoute>} />
      <Route path="/calendrier" element={<ProtectedRoute permission="predictions"><CalendrierMensuel /></ProtectedRoute>} />
      
      {/* Redirect root to dashboard or login */}
      <Route path="/" element={<RootRedirect />} />
      <Route path="*" element={<RootRedirect />} />
    </Routes>
      </React.Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <SplashProvider>
            <CelebrationProvider>
              <AppRoutes />
              <Toaster 
                position="top-right" 
                richColors 
                closeButton
                toastOptions={{
                  style: {
                    fontFamily: 'Manrope, sans-serif',
                  },
                }}
              />
            </CelebrationProvider>
          </SplashProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
